export const clean = (v) =>
  typeof v === "string" ? v.replace(/,+\s*$/g, "").trim() : v ?? "";
export const getName = (u) =>
  clean(
    u?.fullName ||
      u?.name ||
      u?.title ||
      u?.user?.fullName ||
      `${u?.firstName} ${u?.lastName} `
  );
export const getEmail = (u) => clean(u?.email || u?.user?.email);
export const getPhone = (u) =>
  clean(u?.phoneNO || u?.phoneNo || u?.user?.phoneNO);
export const getCreated = (u) => u?.created_at || u?.createdAt || u?.date;
export const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

const OMIT_KEYS = new Set([
  "_id",
  "id",
  "user._id",
  "password",
  "hashedPassword",
  "__v",
  "created_at",
  "createdAt",
  "updated_at",
  "updatedAt",
]);

const PREFERRED_ORDER = [
  "fullName",
  "firstName",
  "lastName",
  "email",
  "phone",
  "phoneNO",
  "phoneNo",
  "verified",
  "isAdmin",
  "date",
];

const isDateLike = (v, key) => {
  if (!v) return false;
  if (v instanceof Date) return true;
  if (typeof v === "number")
    return (
      Number.isFinite(v) && String(key).match(/date|time|created|updated/i)
    );
  if (typeof v === "string") {
    if (String(key).match(/date|time|created|updated/i)) return true;

    if (/^\d{4}-\d{2}-\d{2}T?\d?.*/.test(v)) return true;
  }
  return false;
};

const isPlainObject = (v) =>
  Object.prototype.toString.call(v) === "[object Object]";

const isPrimitiveLike = (v) =>
  v == null ||
  typeof v === "string" ||
  typeof v === "number" ||
  typeof v === "boolean" ||
  v instanceof Date;

const flatten = (obj, prefix = "", out = {}) => {
  if (obj == null) return out;

  if (Array.isArray(obj)) {
    const hasComplex = obj.some((x) => Array.isArray(x) || isPlainObject(x));
    if (hasComplex) {
      obj.forEach((item, i) => {
        const pfx = prefix ? `${prefix}[${i}]` : `[${i}]`;
        flatten(item, pfx, out);
      });
    } else {
      out[prefix] = obj;
    }
    return out;
  }

  if (obj instanceof Date) {
    out[prefix] = obj;
    return out;
  }

  if (isPlainObject(obj)) {
    Object.keys(obj).forEach((k) => {
      const key = prefix ? `${prefix}.${k}` : k;
      flatten(obj[k], key, out);
    });
    return out;
  }

  out[prefix] = obj;
  return out;
};

const formatVal = (v, key) => {
  if (v == null) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";

  if (Array.isArray(v)) {
    return v.map((x) => formatVal(x, key)).join(" | ");
  }

  if (isDateLike(v, key)) {
    const d = v instanceof Date ? v : new Date(v);
    return isNaN(d) ? String(v) : d.toLocaleString();
  }

  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }

  return String(v);
};

const esc = (s) => {
  const str = s == null ? "" : String(s);
  return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
};

const pickHeaderOrder = (allKeys) => {
  const preferred = PREFERRED_ORDER.filter((k) => allKeys.includes(k));
  const rest = allKeys
    .filter((k) => !preferred.includes(k))
    .sort((a, b) => a.localeCompare(b));
  return [...preferred, ...rest];
};

export const exportCsv = (
  finalFiltered,
  range,
  adminFilter,
  verifiedFilter,
  tableName
) => {
  const rows = finalFiltered || [];

  const flatRows = rows.map((r) => flatten(r));
  const keySet = new Set();
  flatRows.forEach((r) => {
    Object.entries(r).forEach(([k, v]) => {
      if (OMIT_KEYS.has(k)) return;

      if (v !== undefined && v !== null && String(v) !== "") keySet.add(k);
    });
  });

  const allKeys = Array.from(keySet);
  if (allKeys.length === 0) {
    return;
  }

  const headers = pickHeaderOrder(allKeys);

  const lines = flatRows.map((r) =>
    headers.map((h) => esc(formatVal(r[h], h))).join(",")
  );

  const csv = [headers.join(","), ...lines].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const [from, to] = range || [];
  const name =
    `${typeof tableName === "string" && tableName ? tableName : "users"}` +
    `_${from ? from.format("YYYY-MM-DD") : "all"}_to_${
      to ? to.format("YYYY-MM-DD") : "all"
    }` +
    `${
      adminFilter in finalFiltered || verifiedFilter in finalFiltered
        ? `_role-${adminFilter || "all"}_ver-${verifiedFilter || "all"}`
        : ""
    }.csv`;

  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
};
